import { cn } from '../lib/utils.js';

interface LoadingProps {
  /** Optional label for screen readers + visual. */
  label?: string;
  /** Compact density — used inside small Card surfaces. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Standard loading indicator. Replaces the three different idioms in the
 * app today: a plain "Loading…" `<p>`, a Skeleton row grid, and silent
 * empty states during fetch. Use this everywhere a CRUD page is waiting
 * on the first query response.
 *
 * For tables / skeleton-shaped placeholders, keep using `<SkeletonRows>` —
 * this component is for the "spinner + label" pattern when a skeleton
 * shape would be premature (e.g. dialog content or full-page load).
 */
export function Loading({
  label = 'Loading…',
  size = 'md',
  className,
}: LoadingProps): JSX.Element {
  const dot = size === 'sm' ? 'h-2 w-2' : 'h-3 w-3';
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-component py-section text-copy text-muted-foreground',
        className
      )}
    >
      <span className="flex gap-1" aria-hidden="true">
        <span
          className={cn('animate-bounce rounded-full bg-current', dot)}
          style={{ animationDelay: '0ms' }}
        />
        <span
          className={cn('animate-bounce rounded-full bg-current', dot)}
          style={{ animationDelay: '150ms' }}
        />
        <span
          className={cn('animate-bounce rounded-full bg-current', dot)}
          style={{ animationDelay: '300ms' }}
        />
      </span>
      <span>{label}</span>
    </div>
  );
}
